/**
 * What a capture run actually recorded, for the Refresh page.
 *
 * The run view could say `balance ok` and nothing more, so confirming a selector
 * read the right figure meant leaving the page for Points -- and since 1.0.0
 * deleted the rule tester there is no other way to see a number at all.
 *
 * The figure shown is what Wallet RECORDED, never what the extension read. The
 * two differ whenever the plausibility hold, the stale refusal or a cross-check
 * disagreement fires, and "two views of one run, disagreeing" is a bug this repo
 * has already paid for twice. So this reads Wallet's own API (`/api/points`,
 * which already feeds the Points page). What moved is the changes card's, from
 * `/api/points/changes`. Nothing is added to the capture port, and the extension
 * never stores a balance.
 */

import type { PointsDto } from "./pointsPage";

export interface RecordedReading {
  /** The ledger's program slug, which is also the registry's and the run's. */
  slug: string;
  program: string;
  /** The account, shown only when a program recorded more than one. */
  label: string;
  /** Server-formatted ("123,456 pts"). Never formatted here: one definition. */
  balance: string;
}

/**
 * The readings a run's programs left on today's balances.
 *
 * Scoped to `capture.as_of === today`, so a balance resting on an older reading
 * or on an email is left out (a reading HELD for review never becomes the
 * balance, so this also, correctly, says nothing about one), and to the slugs
 * the run reported, so checking one program does not list every reading taken
 * today.
 */
export function recordedReadings(dto: PointsDto, slugs: string[]): RecordedReading[] {
  const wanted = new Set(slugs);
  return dto.accounts
    .filter((a) => a.capture?.as_of === dto.today
      && a.row.program_slug !== null
      && wanted.has(a.row.program_slug))
    .map((a) => ({
      slug: a.row.program_slug as string,
      program: a.row.program_name,
      label: a.row.label,
      balance: a.row.balance_text,
    }));
}

/**
 * The slugs a program recorded more than once: the only rows a label tells apart.
 *
 * Per slug, not per list. One program with two accounts used to put a label on
 * every row, so twenty single-account readings each carried a name that
 * distinguished them from nothing.
 */
export function sharedSlugs(readings: RecordedReading[]): Set<string> {
  const seen = new Set<string>();
  const twice = new Set<string>();
  for (const r of readings) {
    if (seen.has(r.slug)) twice.add(r.slug);
    else seen.add(r.slug);
  }
  return twice;
}
